/**
 * SandboxIframe — Generic secure iframe widget for embedding external
 * sandbox environments (StackBlitz WebContainers, CodeSandbox, JupyterLite).
 *
 * Handles: loading states, postMessage communication, resize, error
 * boundary, CSP sandbox attributes, and provider-specific URL building.
 *
 * Used by CodeEditor (Feenix agent portal) and CodeLabPlayer (TeachIQ
 * learner). TeachIQ persists learner source via ScenarioOS
 * ``code_lab_artifacts`` drafts — the iframe remains the execution
 * runtime; Feenix/TeachIQ do not re-execute StackBlitz/JupyterLite code.
 *
 * @widget sandbox_iframe
 * @tier scenario_os
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const PROVIDERS = {
  stackblitz: {
    baseUrl: 'https://stackblitz.com/edit',
    embedUrl: 'https://stackblitz.com/embed',
    sdkUrl: 'https://unpkg.com/@stackblitz/sdk/bundles/sdk.m.js',
    defaultOptions: { forceEmbedLayout: true, hideNavigation: true, hideDevTools: false },
  },
  codesandbox: {
    baseUrl: 'https://codesandbox.io/s',
    embedUrl: 'https://codesandbox.io/embed',
    defaultOptions: { fontsize: 14, hidenavigation: 1, theme: 'dark' },
  },
  jupyterlite: {
    baseUrl: 'https://jupyterlite.github.io/demo',
    embedUrl: 'https://jupyterlite.github.io/demo/repl/index.html',
    defaultOptions: { toolbar: 1, theme: 'JupyterLab Dark' },
  },
  custom: {
    baseUrl: '',
    embedUrl: '',
    defaultOptions: {},
  },
};

const SANDBOX_ATTRS = [
  'allow-scripts',
  'allow-same-origin',
  'allow-popups',
  'allow-forms',
  'allow-modals',
].join(' ');

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    background: '#1e1e2e',
  },
  iframe: {
    width: '100%',
    border: 'none',
    display: 'block',
  },
  loading: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#1e1e2e',
    color: '#94a3b8',
    fontSize: 14,
    zIndex: 2,
  },
  error: {
    padding: 24,
    background: '#fef2f2',
    color: '#dc2626',
    borderRadius: 8,
    fontSize: 14,
    textAlign: 'center',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 12px',
    background: '#1e293b',
    color: '#e2e8f0',
    fontSize: 12,
    fontFamily: 'monospace',
  },
  providerBadge: {
    padding: '2px 8px',
    borderRadius: 4,
    background: '#334155',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
};

export default function SandboxIframe({
  provider = 'stackblitz',
  src,
  projectId,
  files,
  height = 500,
  title = 'Code Sandbox',
  showToolbar = true,
  onMessage,
  onLoad,
  onError,
  sandboxAttrs = SANDBOX_ATTRS,
  className = '',
  style = {},
}) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const providerConfig = PROVIDERS[provider] || PROVIDERS.custom;

  const iframeSrc = useCallback(() => {
    if (src) return src;

    if (provider === 'stackblitz' && projectId) {
      const params = new URLSearchParams({
        embed: '1',
        file: 'index.js',
        hideNavigation: '1',
        ...providerConfig.defaultOptions,
      });
      return `${providerConfig.embedUrl}/${projectId}?${params}`;
    }

    if (provider === 'codesandbox' && projectId) {
      return `${providerConfig.embedUrl}/${projectId}`;
    }

    if (provider === 'jupyterlite') {
      return providerConfig.embedUrl;
    }

    return null;
  }, [src, provider, projectId, providerConfig]);

  useEffect(() => {
    if (!onMessage) return;

    const handler = (event) => {
      if (iframeRef.current && event.source === iframeRef.current.contentWindow) {
        onMessage(event.data, event);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  const handleLoad = useCallback(() => {
    setLoading(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback((e) => {
    setLoading(false);
    const msg = 'Failed to load sandbox environment';
    setError(msg);
    onError?.(msg);
  }, [onError]);

  const resolvedSrc = iframeSrc();

  if (!resolvedSrc) {
    return (
      <div style={styles.error}>
        No sandbox URL configured. Set a <code>src</code>, <code>projectId</code>,
        or configure the <code>{provider}</code> provider.
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.error}>
        {error}
        <button
          onClick={() => { setError(null); setLoading(true); }}
          style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      className={`sandbox-iframe-widget ${className}`}
      style={{ ...styles.container, ...style }}
      role="region"
      aria-label={title}
    >
      {showToolbar && (
        <div style={styles.toolbar}>
          <span>{title}</span>
          <span style={styles.providerBadge}>{provider}</span>
        </div>
      )}

      {loading && (
        <div style={{ ...styles.loading, height }} aria-live="polite">
          Loading {provider} environment…
        </div>
      )}

      <iframe
        ref={iframeRef}
        src={resolvedSrc}
        title={title}
        sandbox={sandboxAttrs}
        allow="clipboard-read; clipboard-write"
        style={{ ...styles.iframe, height }}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}

SandboxIframe.agentDependencies = {
  tools: [],
  skills: ['code_lab_authoring'],
  apis: ['/code-lab/*'],
  collections: ['code_submissions'],
};

export { PROVIDERS, SANDBOX_ATTRS };
